import {
    Box,
    BoxProps,
    Button,
    ButtonProps,
    Grid,
    GridProps,
    useTheme,
    CircularProgress,
    Autocomplete,
    AutocompleteRenderInputParams,
    TextField as MuiTextField,
    Select,
    InputLabel,
    FormControl,
    MenuItem,
} from "@mui/material";
import React from "react";

//? COMPONENTS //
import DatePicker, { DatePickerType } from "components/formsUI/DatePicker";
import SelectNationality, {
    SelectTypes,
} from "components/formsUI/Select/nationality";
import SelectCountry from "components/formsUI/Select/country";
import TextField, { TextfieldTypes } from "components/formsUI/TextField";

//? EXTRA //
import { useTranslation } from "react-i18next";
import countries from "resources/countries_en_nl.json";

//? STYLES //
import * as Styles from "pages/requests/stepsIndividual/step1/step1.styles";
import MultiSelect from "components/formsUI/MultiSelect";
import dayjs, { Dayjs } from "dayjs";
import * as locationService from "services/location-service";
import * as departmentService from "services/department-service";
import * as userService from "services/identityUser-service";
import { AxiosError } from "axios";
import { useField, useFormikContext } from "formik";
import { useParams } from "react-router-dom";
import { isRequired } from "../dataCompletion";
import { useAuthContext } from "contexts/authContext";

//! ------------------------------------------------------------------------------------ //

interface Props {
    titlePath: string;
    //   title: string;
    //   onBackClick: () => void;
    form: any;
    submitDisabled?: boolean;
    error: string;
    isRenew: boolean;
}

function Content(props: Props) {
    const theme = useTheme();
    const { user, checkAuth } = useAuthContext();
    const { t } = useTranslation();
    // const { } = useParams();
    const [dateFromField] = useField("dateFrom");
    // const [dateToField, meta, helpers] = useField("dateTo");
    const [companyNameField] = useField("companyName");
    const { setFieldValue, submitForm, initialValues } = useFormikContext();
    // const [userDepartmentField, meta] = useField('userDepartment');
    const [locations, setLocations] = React.useState<string[]>([]);
    const [departments, setDepartments] = React.useState<string[]>([]);
    const [organisations, setOrganisations] = React.useState<string[]>([]);
    const [disabled, setDisabled] = React.useState<boolean>(false);
    const { titlePath, form, submitDisabled, isRenew } = props;
    const [nationalityField] = useField("userNationality");
    const [departmentField] = useField("userDepartment");

    const getLocationNames = () => {
        locationService
            .getLocationNames("")
            .then((res: string[]) => {
                setLocations(res);
            })
            .catch((error: AxiosError<Error>) => {
                // showError(`${t("certificates.error")}`);
                console.log("error");
            });
    };

    const getDepartmentNames = () => {
        departmentService
            .getDepartmentNames()
            .then((res: string[]) => {
                // setDepartments(res.map((element) => ({ name: element, value: element })));
                setDepartments(res);
                const vals = initialValues as any;
                if (vals.department && res.includes(vals.department)) {
                    console.log("department", vals.department);
                    console.log("department res", res);
                    // setFieldValue('userDepartment', 'FILOG');
                    // setFieldValue('userDepartment', vals.department);
                    // alert(JSON.stringify(initialValues))
                }
            })
            .catch((error: AxiosError<Error>) => {
                // showError(`${t("certificates.error")}`);
                console.log("error");
            });
    };

    React.useEffect(() => {
        if (!locations.length) {
            getLocationNames();
        }
        if (!departments.length) {
            getDepartmentNames();
        }
        setDisabled(false);
        if (
            user !== null &&
            user !== undefined &&
            checkAuth(["External"]) &&
            !checkAuth(["Employee"])
        ) {
            userService
                .getCompanyFromUser()
                .then((res: Company[]) => {
                    setFieldValue("companyName", res[0].name);
                    setOrganisations(res.map((element) => element.name));
                    setDisabled(true);
                    setFieldValue("companyResponsibleFirstName", user.name);
                    setFieldValue("companyResponsibleLastName", user.surname);
                    setFieldValue("companyResponsibleEmail", user.email);
                })
                .catch((error: AxiosError<Error>) => {
                    console.log("error");
                    // no responsible user
                });
        }
    }, []);

    const gridContainerProps: GridProps = {
        container: true,
        spacing: 2,
        minHeight: 500,
    };

    const gridItemFullProps: GridProps = {
        item: true,
        xs: 12,
    };

    const gridItemHalfProps: GridProps = {
        item: true,
        xs: 6,
    };

    const gridItemNameProps: GridProps = {
        item: true,
        xs: 12,
        sm: 6,
        md: 6,
    };

    const titleProps: BoxProps = {
        component: "h1",
        sx: Styles.titleCSS(theme),
    };

    const userDepartmentProps = (
        params: AutocompleteRenderInputParams,
    ): any => {
        return {
            ...params,
            required: isRequired(form, "userDepartment"),
            label: t("requestsTableHeader.department"),
            sx: {
                marginBottom: "1rem",
            },
        };
    };

    const userFirstNameProps: TextfieldTypes = {
        name: "userFirstName",
        required: isRequired(form, "userFirstName"),
        label: t("requestIndividual.step1.firstName"),
    };

    const userLastNameProps: TextfieldTypes = {
        name: "userLastName",
        label: t("requestIndividual.step1.lastName"),
    };

    const userBirthDayProps: DatePickerType = {
        name: "userBirthDay",
        label: t("requestIndividual.step1.birthDate"),
        // minDate: dayjs().subtract(68, 'years'),
        // maxDate: dayjs().subtract(16, 'years')
    };

    const userBirthPlaceProps: TextfieldTypes = {
        name: "userBirthPlace",
        label: t("requestIndividual.step1.birthPlace"),
    };

    const userNationalityProps: SelectTypes = {
        required: isRequired(form, "userNationality"),
        name: "userNationality",
        label: t("requestIndividual.step1.nationality"),
        options: countries,
    };

    const rnnProps: any = {
        name: "userRRN",
        isRRN:
            nationalityField.value &&
            (nationalityField.value == "Belg" ||
                nationalityField.value == "Belgisch" ||
                nationalityField.value == "Belgian")
                ? true
                : false,
        label: t("requestIndividual.step1.rrn"),
    };

    const userCountryProps: SelectTypes = {
        name: "userAddressCountry",
        label: t("requestIndividual.step1.country"),
        options: countries,
    };

    const buttonProps = (buttonName: string): ButtonProps => {
        if (buttonName === "previous") {
            return {
                onClick: () => {
                    console.log("back button clicked");
                },
                variant: "contained",
                sx: Styles.buttonCSS(theme, buttonName),
            };
        } else {
            return {
                onClick: submitForm,
                variant: "contained",
                sx: Styles.buttonCSS(theme, buttonName),
            };
        }
    };

    return (
        <Box sx={Styles.containerCSS(theme)}>
            <Grid {...gridContainerProps}>
                {/* //* Title */}
                <Grid {...gridItemFullProps}>
                    <Box {...titleProps}>{t(titlePath)}</Box>
                </Grid>
                <Grid {...gridItemFullProps}>
                    <Autocomplete
                        options={departments}
                        {...departmentField}
                        onChange={(e: any, value: any) => {
                            setFieldValue("userDepartment", value);
                        }}
                        renderInput={(params) => (
                            <MuiTextField {...userDepartmentProps(params)} />
                        )}
                    />
                </Grid>
                {/* //* First Name */}
                <Grid {...gridItemNameProps}>
                    <TextField {...userFirstNameProps} />
                </Grid>
                <Grid {...gridItemNameProps}>
                    <TextField {...userLastNameProps} />
                </Grid>
                <Grid {...gridItemHalfProps}>
                    <DatePicker {...userBirthDayProps} />
                </Grid>
                <Grid {...gridItemHalfProps}>
                    <TextField {...userBirthPlaceProps} />
                </Grid>
                <Grid {...gridItemHalfProps}>
                    <SelectNationality {...userNationalityProps} />
                </Grid>
                <Grid {...gridItemHalfProps}>
                    <TextField {...rnnProps} />
                </Grid>
                <Grid {...gridItemFullProps}>
                    <TextField
                        required={isRequired(form, "userEmail")}
                        name="userEmail"
                        label={t("requestIndividual.step1.email")}
                    />
                </Grid>
                <Grid {...gridItemFullProps}>
                    <TextField
                        name="userAddressStreet"
                        label={t("requestIndividual.step1.street")}
                    />
                </Grid>
                <Grid {...gridItemHalfProps}>
                    <TextField
                        name="userAddressHouseNumber"
                        label={t("requestIndividual.step1.number")}
                    />
                </Grid>
                <Grid {...gridItemHalfProps}>
                    <TextField
                        name="userAddressBox"
                        label={t("requestIndividual.step1.box")}
                        placeholder={t("requestIndividual.step1.optional")}
                    />
                </Grid>
                <Grid {...gridItemHalfProps}>
                    <TextField
                        name="userAddressZip"
                        label={t("requestIndividual.step1.zip")}
                    />
                </Grid>
                <Grid {...gridItemHalfProps}>
                    <TextField
                        name="userAddressCity"
                        label={t("requestIndividual.step1.city")}
                    />
                </Grid>
                <Grid {...gridItemFullProps}>
                    <SelectCountry {...userCountryProps} />
                </Grid>
                <Grid {...gridItemFullProps}>
                    {organisations.length > 1 ? (
                        <FormControl fullWidth>
                            <InputLabel id="organisation">
                                {t("requestIndividual.step2.organizationName")}
                            </InputLabel>
                            <Select
                                labelId="organisation"
                                label={t(
                                    "requestIndividual.step2.organizationName",
                                )}
                                name="companyName"
                                onChange={(e: any) => {
                                    setFieldValue(
                                        "companyName",
                                        e.target.value,
                                    );
                                }}
                                value={companyNameField.value}
                            >
                                {organisations.map((element) => (
                                    <MenuItem key={element} value={element}>
                                        {element}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <TextField
                            name="companyName"
                            disabled={disabled}
                            label={t(
                                "requestIndividual.step2.organizationName",
                            )}
                        />
                    )}
                </Grid>
                <Grid {...gridItemHalfProps}>
                    <TextField
                        name="companyResponsibleFirstName"
                        label={t(
                            "requestIndividual.step2.responsibleFirstName",
                        )}
                    />
                </Grid>
                <Grid {...gridItemHalfProps}>
                    <TextField
                        name="companyResponsibleLastName"
                        label={t("requestIndividual.step2.responsibleLastName")}
                    />
                </Grid>
                <Grid {...gridItemFullProps}>
                    <TextField
                        name="companyResponsibleEmail"
                        label={t("requestIndividual.step2.responsibleEmail")}
                    />
                </Grid>
                <Grid {...gridItemHalfProps}>
                    <DatePicker
                        required={isRequired(form, "dateFrom")}
                        name="dateFrom"
                        label={t("requestIndividual.step3.dateFrom")}
                        minDate={dayjs()}
                        maxDate={dayjs().add(3, "month")}
                    />
                </Grid>
                {/* Date To */}
                <Grid {...gridItemHalfProps}>
                    <DatePicker
                        required={isRequired(form, "dateTo")}
                        name="dateTo"
                        label={t("requestIndividual.step3.dateTo")}
                        minDate={dayjs(dateFromField.value)}
                        maxDate={dayjs(dateFromField.value).add(1, "year")}
                    />
                    {/* <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <MuiDatePicker
                            value={dateToField.value}
                            onChange={(newValue) => { setFieldValue('dateTo', newValue) }}
                            renderInput={(params: any) => <MuiTextField
                                {...params}
                                name="dateTo"
                                required={isRequired(form, 'dateTo')}
                                error={meta.touched && meta.error}
                                helperText={meta.touched && meta.error}
                            />}
                            label={t("requestIndividual.step3.dateTo")}
                            inputFormat="DD/MM/YYYY"
                            minDate={dayjs(dateFromField.value)}
                            maxDate={dayjs(dateFromField.value).add(1, "year").subtract(1, "day")}
                        />
                    </LocalizationProvider> */}
                </Grid>
                {/* <Grid {...gridItemFullProps} /> */}
                {/* Locations */}
                <Grid {...gridItemFullProps}>
                    <MultiSelect
                        required={isRequired(form, "locations")}
                        name="locations"
                        label={t("requestIndividual.step3.locations")}
                        options={locations}
                    />
                </Grid>
                {/* <Grid {...gridItemFullProps} /> */}
                {/* Reason */}
                <Grid {...gridItemFullProps}>
                    <TextField
                        name="reason"
                        label={t("requestIndividual.step3.reason")}
                        placeholder={t(
                            "requestIndividual.step3.reasonPlaceholder",
                        )}
                    />
                </Grid>
                {/* <Grid {...gridItemFullProps} /> */}
                {/* Function */}
                <Grid {...gridItemFullProps}>
                    <TextField
                        name="function"
                        label={t("requestIndividual.step3.function")}
                        placeholder={t(
                            "requestIndividual.step3.functionPlaceholder",
                        )}
                    />
                </Grid>
                {/* <Grid {...gridItemFullProps}>
                <FileUpload
                    showMessage={showMessage}
                    setFilesUploaded={setFilesUploaded}
                    fileUploads={fileUploadField.value}
                    titleStyle={{display: 'flex', justifyContent: 'center'}}
                    completionHash={completionHash}
                />
            </Grid> */}
            </Grid>

            <Box sx={Styles.buttonContainerCSS(theme)}>
                <Button
                    {...buttonProps("next")}
                    disabled={submitDisabled != null ? submitDisabled : false}
                >
                    {isRenew ? t("renew.completeSubmit") : t("buttons.startup")}
                </Button>
            </Box>

            <Box sx={{ width: "100%", textAlign: "center" }}>
                {submitDisabled === true && <CircularProgress />}
                {props.error !== "" && (
                    <Box color="error.main"> {props.error}</Box>
                )}
            </Box>
        </Box>
    );
}

export default Content;
